import { FC, useLayoutEffect } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';

import useTheme from '@base/useTheme';
import { ChartIntegratedLegend, Layout } from '@common/api';
import { IsolateAbsolute } from '@components/layout/Flex';
import useDebouncedResizeObserver from '@hooks/useDebouncedResizeObserver';
import { useFormatting } from '@providers/FormattingProvider';
import { patch } from '@use-gpu/state';

import { useSharedAxisContext } from './SharedAxisProvider';
import { DEFAULT_CHART } from './constants';
import { ChartData } from './types';
import './echart-imports';

import { formatSeriesTooltip, getTooltipCss, makeFormatNumberAdaptive, makeFormatPercentageLabel } from './formatters';
import { getGridLayout, getValueScale } from './grid';
import {
    mapToEChartSeries,
    mapToWaterfallSpacers,
    mapToWaterfallBridges,
    mapToLabelSeries,
    mapToAxesSeries,
} from './series';
import { resolveStacks, getStackedRange } from './stacks';

const COVER_STYLE = {
    width: '100%',
    height: '100%',
};

export type EChartProps = Partial<ChartData>;

export const EChart: FC<EChartProps> = (props: EChartProps) => {
    const {
        data = [],
        categories = [],
        views,
        stacked = false,
        normalized = false,
        layout = Layout.Horizontal,
        spacing = DEFAULT_CHART.spacing,
        display = DEFAULT_CHART.display,
        background = 'white',
        colorBlind = false,
        formatter,
    } = props;

    const theme = useTheme();
    const formatting = useFormatting();
    const { ref, width, height } = useDebouncedResizeObserver(250, true);

    //// Integrated legend

    const { showIntegratedLegend, showTotals, integratedLegendPosition, shareAxis, shareAxisGroup } = display;

    const hasStartLabels = !!showIntegratedLegend && integratedLegendPosition !== ChartIntegratedLegend.End;
    const hasEndLabels = !!showIntegratedLegend && integratedLegendPosition !== ChartIntegratedLegend.Start;
    const hasStackLabels = !!showTotals;

    //// Grid layout

    const gridLayout = getGridLayout(layout, spacing, display, width, height, hasStartLabels, hasEndLabels, views);

    //// Stacks

    // Divide series over stacks and views
    const { allStacks, allStackIndices, barStacks, stacksByView } = resolveStacks(data, views, stacked);

    // Determine stacked min/max range ahead of time because we need to sync it across multiple views
    const { min, max, totals } = getStackedRange(data, views, allStacks);
    const formatAxisLabel = makeFormatNumberAdaptive(min, max, formatting);
    const formatPercent = makeFormatPercentageLabel(formatting);

    // Determine value scale ahead of time for label hax (value per pixels)
    const ownScale = getValueScale(layout, gridLayout, min, max);

    // Determine shared scale via provider
    const { scales, register } = useSharedAxisContext();
    const hasSharedAxis = !normalized && shareAxis;
    const valueScale = (hasSharedAxis ? scales[layout][shareAxisGroup] : null) ?? ownScale;
    useLayoutEffect(
        () => (hasSharedAxis && ownScale ? register(layout, shareAxisGroup, ownScale) : undefined),
        [hasSharedAxis, layout, shareAxisGroup, ownScale, register]
    );

    const resolvedMin = ownScale ? (min * valueScale) / ownScale : min;
    const resolvedMax = ownScale ? (max * valueScale) / ownScale : max;

    //// Series

    const barGap = `${spacing.barSpacing}%`;
    const allSeries = mapToEChartSeries(
        data,
        normalized,
        layout,
        background,

        totals,
        allStacks,
        barStacks,
        barGap,
        valueScale,
        formatPercent
    );
    const allSpacers = mapToWaterfallSpacers(data, layout, allStacks);
    const allBridges = mapToWaterfallBridges(data, layout, allSpacers.length, allStackIndices, barStacks, barGap);
    const allLabels = mapToLabelSeries(
        data,
        categories,
        views,
        normalized,
        layout,
        background,

        allSpacers.length,

        totals,
        allStacks,
        barStacks,
        stacksByView,

        hasStartLabels,
        hasEndLabels,
        hasStackLabels,
        formatter ?? formatAxisLabel
    );

    //// Domain and value axis

    const isVertical = layout === Layout.Vertical;
    const domainAxis = isVertical ? 'yAxis' : 'xAxis';
    const valueAxis = isVertical ? 'xAxis' : 'yAxis';

    const showDomainAxis = display.showDomainAxis;
    const showValueAxis = display.showValueAxis && (isVertical ? !hasEndLabels : !hasStartLabels);
    const domainAxisLabelAngle = display.domainAxisLabelAngle;
    const domainAxisLabelCompact = display.domainAxisLabelCompact;

    const domainAxisProps = {
        type: 'category',
        show: true,
        data: categories,
        axisLine: {
            lineStyle: {
                color: display.domainAxisColor,
                width: 1,
            },
        },
        axisLabel: {
            color: theme.colors.ui.dark,
            hideOverlap: true,
            fontFamily: 'Inter',
            formatter: !showDomainAxis
                ? () => ''
                : domainAxisLabelCompact
                ? (l: string) => l.replaceAll(/ /g, '\n')
                : undefined,
            rotate: domainAxisLabelAngle,
        },
        axisTick: {
            show: showDomainAxis,
            alignWithLabel: true,
        },
        inverse: isVertical,
    };

    const valueAxisProps = {
        type: 'value',
        show: showValueAxis,
        axisLabel: {
            hideOverlap: true,
            fontFamily: 'Inter',
            formatter: !showValueAxis ? () => '' : formatAxisLabel,
        },
        min: normalized ? 0 : resolvedMin,
        max: normalized ? 1 : resolvedMax,
    };

    const allAxes = mapToAxesSeries(views, layout, background, display.domainAxisColor, display.domainAxisBorderStyle);

    // Combined series
    const series = [...allSpacers, ...allSeries, ...allBridges, ...allLabels, ...allAxes];

    const echartConfig = {
        animation: true,
        animationDuration: 500,
        series,
        tooltip: {
            confine: true,
            trigger: 'axis',
            formatter: formatSeriesTooltip,
            axisPointer: {
                type: 'shadow',
            },
            extraCssText: getTooltipCss(theme),
        },
        legend: {
            show: display.showLegend,
            bottom: 0,
            type: 'scroll',
            // simplify item style, color needs to be constant
            data: allSeries.map((s) =>
                patch(s, {
                    itemStyle: {
                        color: s.itemStyle.fillColor,
                    },
                })
            ),
            textStyle: {
                fontFamily: 'Inter',
            },
        },
        ...gridLayout,
        [domainAxis]: views?.map((v, i) => ({ ...domainAxisProps, gridIndex: i })) ?? domainAxisProps,
        [valueAxis]: views?.map((v, i) => ({ ...valueAxisProps, gridIndex: i })) ?? valueAxisProps,
        aria: {
            enabled: true,
            decal: {
                show: colorBlind,
            },
        },
    };

    return (
        <IsolateAbsolute ref={ref}>
            {data.length ? (
                <ReactEChartsCore
                    echarts={echarts}
                    option={echartConfig}
                    style={COVER_STYLE}
                    lazyUpdate={true}
                    notMerge={true}
                />
            ) : null}
        </IsolateAbsolute>
    );
};
